// A quote as a bundle of jobs (finance-real, the user 2026-10-22), the start of the client's history: once accepted its
// jobs are the client's agreed jobs, each started on its own from its status (the user 2026-10-28: the work a client says
// yes to can start months apart). Each drawer is named in the URL (DD-045, DD-051).
// ?modal=quote&quote=<id>: its status, the platform's status dropdown the user sets (DD-062; the user: "rather than
// decline it's just a status": draft, proposed, accepted, declined; Accepted asks nothing and starts nothing), its
// jobs as lines (the shared JobLine), each opening its job's drawer in place of this one, its status the job's own
// dropdown (Active for a job not started opens its drawer asking the day, `confirm=start`), Add draft jobs at that list (the
// invoice drawer's checkbox picker, JobPicks, over the client's drafts on no other quote) or New job, its totals (the
// server's, TD-102), and Edit.
// ?modal=quote-form: New quote, or with `quote` its edit: a title and the client's draft jobs to put on it (JobPicks).
// A new job from either stacks the job form over it (`stack=line-job-form`, the user: "if I create a new job it stacks the
// job modal"): the one exception to one modal at a time (DD-045), as the quote under it is still being written. The job
// comes back on the quote. The agency's own client takes no quote (TD-105), so the page never opens these.
import { useState, type ReactNode } from "react";
import { Button } from "@/components/ui/Button";
import { FormField, inputClassName } from "@/components/ui/FormField";
import { MoneyText } from "@/components/ui/MoneyText";
import { ENTITY_CONFIG, EntityCreateButton } from "@/core/entityConfig";
import { FinanceDrawer } from "../components/FinanceDrawer";
import { JobLine } from "../components/JobLine";
import { useSubmitOnce } from "../hooks/useSubmitOnce";
import type { LineJob, QuoteBundle, QuoteBundleStatus } from "../types";
import { dateLabel } from "../utils/format";
import { closeUnlessPending } from "../utils/mutations";
import { jobSubline } from "../utils/names";
import { useQuoteDrawer } from "./hooks/useQuoteDrawer";
import { useQuoteFormDrawer } from "./hooks/useQuoteFormDrawer";
import { useStatusPicker } from "./hooks/useStatusPicker";
import { JobPicks, toggled } from "./JobInvoiceDrawers";
import { JobStatusBadge } from "./JobStatusBadge";
import { needsStart } from "./LineJobDrawer";
import { LineJobFormDrawer } from "./LineJobForms";
import { listPriceText, QUOTE_STATUS, QUOTE_STATUSES } from "./lineJobText";
import { StatusBadge } from "./StatusBadge";

// `contract` names the user's job now (the entityConfig shadow).
const JOB = ENTITY_CONFIG.contract;
const jobWord = JOB.label.toLowerCase();
const jobsWord = JOB.plural.toLowerCase();
const QUOTE = ENTITY_CONFIG.quote;
const quoteWord = QUOTE.label.toLowerCase();
const heading = "text-[11px] uppercase tracking-wide text-gray-500 mb-1";

/** What each status does to the jobs on it, beside the picker. */
const STATUS_NOTE: Record<QuoteBundleStatus, string> = {
  draft: `Its ${jobsWord} are drafts.`,
  proposed: `Its ${jobsWord} are quoting.`,
  accepted: `Its ${jobsWord} are agreed: each starts from its own status.`,
  declined: `Its ${jobsWord} are dropped.`,
};

function Figure({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="mt-0.5 text-sm font-semibold tabular-nums text-gray-900">{children}</div>
    </div>
  );
}


/** What the job form stacked over a quote needs. */
interface Stack {
  clientId: string;
  today: string;
  stacked: boolean;
  onStack: () => void;
  onUnstack: () => void;
}

interface QuoteProps extends Stack {
  quote: QuoteBundle;
  /** Its jobs, in the quote's order. */
  jobs: LineJob[];
  /** The client's draft jobs on no quote: what Add a job picks from. */
  drafts: LineJob[];
  /** Its drawer in place of this one; `starting` asks the day it starts at once (`confirm=start`). */
  onOpenJob: (jobId: string, starting?: boolean) => void;
  onEdit: () => void;
  onClose: () => void;
}

interface QuoteStatusProps {
  quote: QuoteBundle;
  onPick: (status: QuoteBundleStatus) => void;
  loading?: boolean;
  align?: "left" | "right";
}

/** A quote's status dropdown, on its row and in its drawer (DD-062). A quote with no job goes no further than Draft or
 * Declined, as the server has it. Accepted goes back to any other while every job on it is still agreed (a mis-click);
 * once one has moved on the server refuses it, and its refusal is the toast every refused status gets. */
export function QuoteStatusBadge({ quote, onPick, loading = false, align = "left" }: QuoteStatusProps) {
  return (
    <StatusBadge<QuoteBundleStatus>
      value={quote.status}
      choices={QUOTE_STATUSES.map((s) => ({ value: s, look: QUOTE_STATUS[s], disabled: quote.jobIds.length === 0 && s !== "draft" && s !== "declined" }))}
      onPick={onPick}
      loading={loading}
      align={align}
      ariaLabel={`${quote.title} status`}
    />
  );
}

/** The status, set by the user: every pick is one write, Accepted too, which asks no day. */
function StatusPicker({ quote }: { quote: QuoteBundle }) {
  const { set } = useStatusPicker();
  return (
    <div className="flex flex-wrap items-center gap-2">
      <QuoteStatusBadge quote={quote} onPick={(status) => set.mutate({ quoteId: quote.id, input: { status, startsOn: null } })} loading={set.isPending} />
      <span className="text-xs text-gray-500">{[quote.proposedOn ? `Proposed ${dateLabel(quote.proposedOn)}.` : null, STATUS_NOTE[quote.status]].filter(Boolean).join(" ")}</span>
    </div>
  );
}

export function QuoteDrawer({ quote, jobs, drafts, clientId, today, stacked, onStack, onUnstack, onOpenJob, onEdit, onClose }: QuoteProps) {
  const { update, setJobStatus } = useQuoteDrawer();
  const [adding, setAdding] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const open = quote.status === "draft" || quote.status === "proposed";
  return (
    <>
      <FinanceDrawer
        onClose={closeUnlessPending(update.isPending, onClose)}
        title={quote.title}
        footer={
          <Button variant="secondary" onClick={onEdit}>
            {`Edit ${quoteWord}`}
          </Button>
        }
      >
        <div className="space-y-5 text-sm">
          <StatusPicker key={quote.status} quote={quote} />

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Figure label="Each month">
              <MoneyText amountMinor={quote.monthlyMinor} />
            </Figure>
            <Figure label="Margin each month">
              <MoneyText amountMinor={quote.monthlyMarginMinor} balance />
            </Figure>
            <Figure label="One-off">
              <MoneyText amountMinor={quote.oneOffMinor} />
            </Figure>
            <Figure label="Margin one-off">
              <MoneyText amountMinor={quote.oneOffMarginMinor} balance />
            </Figure>
          </div>

          <section className="space-y-2">
            <h3 className={heading}>{JOB.plural}</h3>
            {jobs.length === 0 ? (
              <p className="text-gray-400">{`None yet. Add a ${jobWord} to propose it.`}</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {jobs.map((job) => (
                  <li key={job.id}>
                    {/* Its status is the shared dropdown (DD-062), beside the line, not inside its button. */}
                    <JobLine
                      name={job.name}
                      sub={jobSubline(job)}
                      note={listPriceText(job)}
                      amountMinor={job.priceMinor}
                      onOpen={() => onOpenJob(job.id)}
                      trailing={
                        <JobStatusBadge
                          job={job}
                          onOpenQuote={open}
                          align="right"
                          loading={setJobStatus.isPending && setJobStatus.variables?.jobId === job.id}
                          onPick={(s) => (s === "active" && needsStart(job) ? onOpenJob(job.id, true) : setJobStatus.mutate({ jobId: job.id, input: { status: s, startsOn: null } }))}
                        />
                      }
                    />
                  </li>
                ))}
              </ul>
            )}
            {/* The invoice drawer's Add jobs, over the client's drafts on no quote, and New job (DD-056). */}
            {open && !adding && (
              <div className="flex flex-wrap items-center gap-2 pt-1">
                {drafts.length > 0 && (
                  <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
                    {`Add draft ${jobsWord}`}
                  </Button>
                )}
                <EntityCreateButton entity="contract" variant="secondary" onClick={onStack} />
              </div>
            )}
            {open && adding && (
              <div className="space-y-2 rounded-lg bg-gray-50 p-3">
                <JobPicks jobs={drafts} picked={picked} onToggle={(id) => setPicked((p) => toggled(p, id))} disabled={update.isPending} />
                {update.error && <p className="text-xs text-red-600">{update.error.message}</p>}
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="ghost" disabled={update.isPending} onClick={() => setAdding(false)}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    disabled={picked.length === 0 || update.isPending}
                    isLoading={update.isPending}
                    onClick={() =>
                      update.mutate(
                        { quoteId: quote.id, input: { jobIds: [...quote.jobIds, ...picked] } },
                        {
                          onSuccess: () => {
                            setPicked([]);
                            setAdding(false);
                          },
                        },
                      )
                    }
                  >
                    {`Add to the ${quoteWord}`}
                  </Button>
                </div>
              </div>
            )}
          </section>
        </div>
      </FinanceDrawer>
      {stacked && open && <LineJobFormDrawer clientId={clientId} forQuote={{ quoteId: quote.id, title: quote.title }} own={false} today={today} onSaved={onUnstack} onClose={onUnstack} />}
    </>
  );
}

interface FormProps extends Stack {
  /** The quote to edit; absent for a new one. */
  quote?: QuoteBundle;
  /** The client's jobs a quote may hold: its own, then the drafts on no quote. */
  choices: LineJob[];
  onSaved: (quoteId: string) => void;
  onClose: () => void;
}

/** New quote, or an edit: its title and its jobs, ticked from the client's drafts. A new job stacks the job form and
 * comes back ticked. */
export function QuoteFormDrawer({ clientId, quote, choices, today, stacked, onStack, onUnstack, onSaved, onClose }: FormProps) {
  const { create, update } = useQuoteFormDrawer(clientId);
  const send = useSubmitOnce();
  const [title, setTitle] = useState(quote?.title ?? "");
  const [picked, setPicked] = useState<string[]>(quote?.jobIds ?? []);
  const [touched, setTouched] = useState(false);
  const pending = create.isPending || update.isPending;
  // An accepted or declined quote's jobs live their own lives: only its title changes.
  const jobsChange = !quote || quote.status === "draft" || quote.status === "proposed";
  const error = title.trim() === "" ? "A title is required" : undefined;
  const submit = () => {
    setTouched(true);
    if (error) return;
    const input = jobsChange ? { title: title.trim(), jobIds: picked } : { title: title.trim() };
    if (quote) send(() => update.mutateAsync({ quoteId: quote.id, input }), (q) => onSaved(q.id));
    else send(() => create.mutateAsync({ title: title.trim(), jobIds: picked }), (q) => onSaved(q.id));
  };
  return (
    <>
      <FinanceDrawer
        title={quote ? `Edit · ${quote.title}` : QUOTE.createLabel}
        onClose={closeUnlessPending(pending, onClose)}
        footer={
          <Button disabled={pending} isLoading={pending} onClick={submit}>
            {quote ? "Save" : "Make the draft"}
          </Button>
        }
      >
        <div className="space-y-4">
          <FormField label="Title" hint="What the client reads it as." error={touched ? error : undefined}>
            <input aria-label="Title" className={inputClassName} value={title} maxLength={120} placeholder="Social media" onChange={(e) => setTitle(e.target.value)} />
          </FormField>
          <section className="space-y-2">
            <h3 className={heading}>{JOB.plural}</h3>
            {quote && !jobsChange ? (
              <p className="text-xs text-gray-500">{`The ${quoteWord} is ${QUOTE_STATUS[quote.status].label.toLowerCase()}: its ${jobsWord} live their own lives now.`}</p>
            ) : (
              <>
                {choices.length === 0 ? (
                  <p className="text-gray-400">{`No draft ${jobWord} on the ${JOB.plural} tab yet. Make one here.`}</p>
                ) : (
                  <JobPicks jobs={choices} picked={picked} onToggle={(id) => setPicked((p) => toggled(p, id))} />
                )}
                <EntityCreateButton entity="contract" variant="secondary" onClick={onStack} />
              </>
            )}
          </section>
        </div>
      </FinanceDrawer>
      {stacked && jobsChange && (
        <LineJobFormDrawer
          clientId={clientId}
          // A quote still being written has no id: the job is a draft on no quote, ticked here, and goes on it on save.
          forQuote={{ quoteId: null, title: title.trim() || QUOTE.createLabel }}
          own={false}
          today={today}
          onSaved={(job) => {
            setPicked((p) => [...p, job.id]);
            onUnstack();
          }}
          onClose={onUnstack}
        />
      )}
    </>
  );
}
